import { useEffect, useMemo, useState, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  SlidersHorizontal,
  ArrowUpDown,
  LayoutGrid,
  LayoutList,
  Plus,
  CheckSquare,
  Square,
  Truck,
  PackageCheck,
  RotateCcw,
  ShoppingBag,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  LoadingState,
  ErrorState,
  EmptyState,
} from '@/components/ui';
import { PickerSheet, PickerRow } from './components/PickerSheet';
import { StatusBadge } from './components/StatusBadge';
import {
  SendForServiceModal,
  MarkReturnedModal,
  MarkDeliveredModal,
  MarkPurchasedModal,
} from './components/transitionModals';
import api from '@/lib/api';
import { useMutation } from '@/hooks/useApi';
import { COLOR_HEX } from '@/lib/constants';
import { buildQueryString, cn } from '@/lib/utils';
import type { Item, ItemStatus } from '@/lib/types';

type SortValue = 'name' | 'acquired' | 'wears' | 'cleaned' | 'price-desc' | 'price-asc';

const SORT_OPTIONS: { label: string; value: SortValue }[] = [
  { label: 'Name', value: 'name' },
  { label: 'Date Acquired', value: 'acquired' },
  { label: 'Number of Wears', value: 'wears' },
  { label: 'Last Cleaned', value: 'cleaned' },
  { label: 'Price (High to Low)', value: 'price-desc' },
  { label: 'Price (Low to High)', value: 'price-asc' },
];

function sortToParams(sort: SortValue): { sort: string; order: string } {
  switch (sort) {
    case 'acquired':
      return { sort: 'acquired', order: 'desc' };
    case 'wears':
      return { sort: 'wears', order: 'desc' };
    case 'cleaned':
      return { sort: 'cleaned', order: 'desc' };
    case 'price-desc':
      return { sort: 'price', order: 'desc' };
    case 'price-asc':
      return { sort: 'price', order: 'asc' };
    case 'name':
    default:
      return { sort: 'name', order: 'asc' };
  }
}

const SECTION_LABELS: Record<string, string> = {
  'in-closet': 'In Closet',
  'out-for-service': 'Out for Service',
  'pending-delivery': 'Pending Delivery',
  wishlist: 'Wishlist',
  'wishlist-archive': 'Wishlist Archive',
  owned: 'In Closet',
  'for-sale': 'For Sale',
  archived: 'Removed',
  all: 'All Items',
};

// Map a URL ?sort= / ?order= back to the local SortValue.
function paramsToSort(sort: string | null, order: string | null): SortValue {
  if (sort === 'acquired') return 'acquired';
  if (sort === 'wears') return 'wears';
  if (sort === 'cleaned') return 'cleaned';
  if (sort === 'price') return order === 'asc' ? 'price-asc' : 'price-desc';
  return 'name';
}

interface ItemsResponse {
  items: Item[];
  total: number;
}

export function WardrobeItemsPage() {
  const [searchParams] = useSearchParams();
  const { mutate } = useMutation();

  const status = searchParams.get('status') ?? 'all';
  const unworn = searchParams.get('unworn') === '1';
  const categoryParam = searchParams.get('category') ?? '';
  const q = searchParams.get('q') ?? '';
  const storeId = searchParams.get('store_id') ?? '';
  const storeName = searchParams.get('store_name') ?? '';
  const brand = searchParams.get('brand') ?? '';

  const baseTitle = unworn ? 'Unworn Report' : (SECTION_LABELS[status] ?? 'Items');
  const contextName = storeName || brand;
  const pageTitle = contextName ? `${baseTitle} · ${contextName}` : baseTitle;

  const [items, setItems] = useState<Item[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showFilter, setShowFilter] = useState(false);
  const [showSort, setShowSort] = useState(false);
  // Seed sort from the URL so deep links (e.g. "by date acquired, newest") apply.
  const [sort, setSort] = useState<SortValue>(() =>
    paramsToSort(searchParams.get('sort'), searchParams.get('order')),
  );
  const [viewMode, setViewMode] = useState<'expanded' | 'compact'>('expanded');

  // Multi-vector client-side filters: status, category, color (any combination).
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(
    () => new Set(categoryParam ? [categoryParam] : []),
  );
  const [colorFilter, setColorFilter] = useState<Set<string>>(new Set());

  const toggle = (setter: Dispatch<SetStateAction<Set<string>>>) => (val: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });

  const activeFilterCount = statusFilter.size + categoryFilter.size + colorFilter.size;

  // Multi-select batch mode (in-closet only)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchService, setShowBatchService] = useState(false);

  // Single-item action modals
  const [actionItem, setActionItem] = useState<Item | null>(null);
  const [actionType, setActionType] = useState<
    'service' | 'returned' | 'delivered' | 'purchased' | null
  >(null);
  const [actionBusy, setActionBusy] = useState(false);

  const { sortKey, orderKey } = useMemo(() => {
    const p = sortToParams(sort);
    return { sortKey: p.sort, orderKey: p.order };
  }, [sort]);

  const queryString = useMemo(
    () =>
      buildQueryString({
        status: status !== 'all' ? status : undefined,
        unworn: unworn ? '1' : undefined,
        q: q || undefined,
        store_id: storeId || undefined,
        brand: brand || undefined,
        sort: sortKey,
        order: orderKey,
      }),
    [status, unworn, q, storeId, brand, sortKey, orderKey],
  );

  const load = useMemo(
    () => async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get<ItemsResponse>(`/items${queryString}`);
        setItems(res.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load items');
      } finally {
        setLoading(false);
      }
    },
    [queryString],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Reset select mode when leaving in-closet view
  useEffect(() => {
    if (status !== 'in-closet') {
      setSelectMode(false);
      setSelectedIds(new Set());
    }
  }, [status]);

  // Distinct values present in the loaded set, for the filter sheet options.
  const availableStatuses = useMemo(
    () => Array.from(new Set((items ?? []).map((i) => i.status))),
    [items],
  );
  const availableCategories = useMemo(
    () => Array.from(new Set((items ?? []).map((i) => i.category).filter(Boolean))).sort(),
    [items],
  );
  const availableColors = useMemo(
    () =>
      Array.from(new Set((items ?? []).map((i) => i.color).filter((c): c is string => !!c))).sort(),
    [items],
  );

  const filtered = useMemo(
    () =>
      (items ?? []).filter(
        (it) =>
          (statusFilter.size === 0 || statusFilter.has(it.status)) &&
          (categoryFilter.size === 0 || categoryFilter.has(it.category)) &&
          (colorFilter.size === 0 || (it.color != null && colorFilter.has(it.color))),
      ),
    [items, statusFilter, categoryFilter, colorFilter],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const item of filtered) {
      const key = item.category || 'Uncategorized';
      const arr = map.get(key) ?? [];
      arr.push(item);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAction = (item: Item, type: NonNullable<typeof actionType>) => {
    setActionItem(item);
    setActionType(type);
  };

  const closeAction = () => {
    setActionItem(null);
    setActionType(null);
    setActionBusy(false);
  };

  const runTransition = async (action: string, payload: Record<string, unknown>) => {
    if (!actionItem) return;
    setActionBusy(true);
    const res = await mutate('post', `/items/${actionItem.id}/transition`, { action, ...payload });
    setActionBusy(false);
    if (res !== null) {
      closeAction();
      load();
    }
  };

  const submitBatchService = async (payload: Record<string, unknown>) => {
    const itemIds = Array.from(selectedIds);
    if (itemIds.length === 0) return;
    const res = await mutate('post', '/service-batches', { itemIds, ...payload });
    if (res !== null) {
      setShowBatchService(false);
      setSelectMode(false);
      setSelectedIds(new Set());
      load();
    }
  };

  return (
    <Page>
      <PageHeader section={pageTitle} backHref="/wardrobe" backLabel="Wardrobe" />

      {/* Controls bar */}
      <div className="sticky top-[73px] z-10 bg-background/90 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-2 overflow-x-auto">
          <Link to="/wardrobe/add" className="flex-shrink-0">
            <Button className="px-3 py-2">
              <Plus className="w-4 h-4" />
              Add
            </Button>
          </Link>
          <Button
            variant={activeFilterCount > 0 ? 'primary' : 'outline'}
            className="px-3 py-2 flex-shrink-0"
            onClick={() => setShowFilter(true)}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
          <Button
            variant={sort !== 'name' ? 'primary' : 'outline'}
            className="px-3 py-2 flex-shrink-0"
            onClick={() => setShowSort(true)}
          >
            <ArrowUpDown className="w-4 h-4" />
            {sort !== 'name' ? (SORT_OPTIONS.find((o) => o.value === sort)?.label ?? 'Sort') : 'Sort'}
          </Button>
          {sort !== 'name' && (
            <Button
              variant="outline"
              className="px-2.5 py-2 flex-shrink-0"
              onClick={() => setSort('name')}
              title="Clear sort"
              aria-label="Clear sort"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
          {status === 'in-closet' && (
            <Button
              variant={selectMode ? 'primary' : 'outline'}
              className="px-3 py-2 flex-shrink-0"
              onClick={() => {
                setSelectMode((v) => !v);
                setSelectedIds(new Set());
              }}
            >
              {selectMode ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              Select
            </Button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setViewMode((v) => (v === 'expanded' ? 'compact' : 'expanded'))}
            className="p-2 bg-card border border-border rounded-xl text-foreground hover:border-primary/30 transition-all flex-shrink-0"
            aria-label="Toggle view"
          >
            {viewMode === 'expanded' ? (
              <LayoutList className="w-5 h-5" />
            ) : (
              <LayoutGrid className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      <Main className="space-y-6">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && items && filtered.length === 0 && (
          <EmptyState
            icon={<ShoppingBag className="w-8 h-8" />}
            title="No items found"
            description="Try adjusting your filters, or add a new item to your wardrobe."
            action={
              <Link to="/wardrobe/add">
                <Button>
                  <Plus className="w-4 h-4" />
                  Add Item
                </Button>
              </Link>
            }
          />
        )}

        {!loading &&
          !error &&
          grouped.map(([category, catItems]) => (
            <section key={category}>
              <h2 className="text-base font-semibold text-foreground uppercase tracking-wide mb-3 font-display">
                {category} ({catItems.length})
              </h2>
              <div className="space-y-3">
                {catItems.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    viewMode={viewMode}
                    selectMode={selectMode}
                    selected={selectedIds.has(item.id)}
                    onToggleSelect={() => toggleSelect(item.id)}
                    onAction={openAction}
                  />
                ))}
              </div>
            </section>
          ))}
      </Main>

      {/* Batch action bar */}
      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-20 inset-x-0 z-30 px-4">
          <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl shadow-lg p-3 flex items-center gap-3">
            <span className="text-sm font-medium text-foreground">
              {selectedIds.size} selected
            </span>
            <div className="flex-1" />
            <Button onClick={() => setShowBatchService(true)}>
              <Truck className="w-4 h-4" />
              Send for Service
            </Button>
          </div>
        </div>
      )}

      {/* Filter sheet — multi-select across status, category and color. */}
      <PickerSheet
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        title="Filter"
        footer={
          <div className="flex gap-2">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setStatusFilter(new Set());
                setCategoryFilter(new Set());
                setColorFilter(new Set());
              }}
              disabled={activeFilterCount === 0}
            >
              Clear all
            </Button>
            <Button fullWidth onClick={() => setShowFilter(false)}>
              Done
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Status — only on the all-items view (specific views are already one status). */}
          {status === 'all' && availableStatuses.length > 1 && (
            <FilterSection title="Status">
              {availableStatuses.map((s) => (
                <PickerRow
                  key={s}
                  label={SECTION_LABELS[s] ?? s}
                  selected={statusFilter.has(s)}
                  onClick={() => toggle(setStatusFilter)(s)}
                />
              ))}
            </FilterSection>
          )}

          {availableCategories.length > 0 && (
            <FilterSection title="Category">
              {availableCategories.map((cat) => (
                <PickerRow
                  key={cat}
                  label={cat}
                  selected={categoryFilter.has(cat)}
                  onClick={() => toggle(setCategoryFilter)(cat)}
                />
              ))}
            </FilterSection>
          )}

          {availableColors.length > 0 && (
            <FilterSection title="Color">
              {availableColors.map((color) => (
                <PickerRow
                  key={color}
                  label={
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-3.5 h-3.5 rounded-full border border-border flex-shrink-0"
                        style={{ backgroundColor: COLOR_HEX[color] ?? '#ccc' }}
                      />
                      {color}
                    </span>
                  }
                  selected={colorFilter.has(color)}
                  onClick={() => toggle(setColorFilter)(color)}
                />
              ))}
            </FilterSection>
          )}
        </div>
      </PickerSheet>

      {/* Sort sheet */}
      <PickerSheet isOpen={showSort} onClose={() => setShowSort(false)} title="Sort By">
        {SORT_OPTIONS.map((opt) => (
          <PickerRow
            key={opt.value}
            label={opt.label}
            selected={sort === opt.value}
            onClick={() => {
              setSort(opt.value);
              setShowSort(false);
            }}
          />
        ))}
      </PickerSheet>

      {/* Batch send-for-service modal */}
      <SendForServiceModal
        isOpen={showBatchService}
        onClose={() => setShowBatchService(false)}
        itemName={`${selectedIds.size} items`}
        onConfirm={(payload) => submitBatchService(payload)}
      />

      {/* Single-item action modals */}
      {actionItem && (
        <>
          <SendForServiceModal
            isOpen={actionType === 'service'}
            onClose={closeAction}
            itemName={actionItem.name}
            busy={actionBusy}
            onConfirm={(payload) => runTransition('send-for-service', payload)}
          />
          <MarkReturnedModal
            isOpen={actionType === 'returned'}
            onClose={closeAction}
            itemName={actionItem.name}
            busy={actionBusy}
            onConfirm={(payload) => runTransition('mark-returned', payload)}
          />
          <MarkDeliveredModal
            isOpen={actionType === 'delivered'}
            onClose={closeAction}
            itemName={actionItem.name}
            busy={actionBusy}
            onConfirm={(payload) => runTransition('mark-delivered', payload)}
          />
          <MarkPurchasedModal
            isOpen={actionType === 'purchased'}
            onClose={closeAction}
            itemName={actionItem.name}
            busy={actionBusy}
            onConfirm={(payload) => runTransition('mark-purchased', payload)}
          />
        </>
      )}
    </Page>
  );
}

function ItemRow({
  item,
  viewMode,
  selectMode,
  selected,
  onToggleSelect,
  onAction,
}: {
  item: Item;
  viewMode: 'expanded' | 'compact';
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onAction: (item: Item, type: 'service' | 'returned' | 'delivered' | 'purchased') => void;
}) {
  const actions = getActions(item.status);
  // Brand line: bespoke items read "Made By: {brand} (cloth: {mill})".
  const brandLine = item.isBespoke
    ? `Made By: ${item.brand ?? '—'}${item.mill ? ` (cloth: ${item.mill})` : ''}`
    : item.brand ?? '—';
  const materialLine = item.materials.length ? item.materials.join(', ') : null;

  const inner = (
    <Card className={cn('p-3', selectMode && selected && 'border-primary')}>
      <div className="flex gap-3">
        {selectMode && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleSelect();
            }}
            className="flex-shrink-0 self-start mt-1"
            aria-label="Select item"
          >
            {selected ? (
              <CheckSquare className="w-5 h-5 text-primary" />
            ) : (
              <Square className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
        )}
        <CardPhotos item={item} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground leading-tight line-clamp-2">
              {item.name}
            </h3>
            {/* Location/status only when the item isn't simply in the closet. */}
            {item.status !== 'in-closet' && <StatusBadge status={item.status} />}
          </div>

          {viewMode === 'expanded' ? (
            <div className="mt-1.5 space-y-0.5">
              <p className="text-sm text-foreground font-medium truncate">{brandLine}</p>
              {item.color && <p className="text-xs text-muted-foreground truncate">{item.color}</p>}
              {materialLine && (
                <p className="text-xs text-muted-foreground truncate">{materialLine}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
              {[item.isBespoke ? brandLine : item.brand, item.color].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      </div>

      {!selectMode && actions.length > 0 && (
        <div className="flex gap-4 mt-3 pt-3 border-t border-border">
          {actions.map((action) => (
            <button
              key={action.type}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAction(item, action.type);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
            >
              <action.icon className="w-3.5 h-3.5" />
              {action.label}
            </button>
          ))}
        </div>
      )}
    </Card>
  );

  if (selectMode) {
    return <div>{inner}</div>;
  }
  return <Link to={`/wardrobe/item/${item.id}`}>{inner}</Link>;
}

function FilterSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide px-4 mb-1 font-display">
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
}

// Prominent card image: a horizontally scrollable carousel of the item's photos
// with a count badge. Falls back to the single primary photo, then a placeholder.
function CardPhotos({ item }: { item: Item }) {
  const photos: { id: string; url: string; thumbnailUrl: string | null }[] =
    item.photos && item.photos.length > 0
      ? item.photos
      : item.primaryPhotoUrl
        ? [{ id: 'primary', url: item.primaryPhotoUrl, thumbnailUrl: item.primaryPhotoUrl }]
        : [];
  const count = photos.length;

  return (
    <div className="relative w-28 h-28 flex-shrink-0 rounded-xl overflow-hidden bg-secondary">
      {count === 0 ? (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          <ShoppingBag className="w-6 h-6" />
        </div>
      ) : (
        <div className="flex w-full h-full overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {photos.map((p) => (
            <img
              key={p.id}
              src={p.thumbnailUrl ?? p.url}
              alt=""
              className="w-28 h-28 object-cover flex-shrink-0 snap-center"
            />
          ))}
        </div>
      )}
      {count > 1 && (
        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-foreground/70 text-white text-[10px] font-medium rounded-full">
          {count}
        </span>
      )}
    </div>
  );
}

function getActions(
  status: ItemStatus,
): { type: 'service' | 'returned' | 'delivered' | 'purchased'; label: string; icon: typeof Truck }[] {
  switch (status) {
    case 'in-closet':
      // Send-for-service moved to the multi-select batch flow; no per-card action.
      return [];
    case 'out-for-service':
      return [{ type: 'returned', label: 'Mark Returned', icon: RotateCcw }];
    case 'pending-delivery':
      return [{ type: 'delivered', label: 'Mark Delivered', icon: PackageCheck }];
    case 'wishlist':
      return [{ type: 'purchased', label: 'Mark Purchased', icon: ShoppingBag }];
    default:
      return [];
  }
}
